// SEO Score Calculator - Real implementation using Google Search Console data
// Uses data from your Supabase analytics_data and seo_audits tables

export interface SEOScoreInput {
  // From Google Search Console (analytics_data table)
  avgPosition: number;
  totalClicks: number;
  totalImpressions: number;
  avgCTR: number;
  
  // From basic page analysis (seo_audits table)
  hasSSL: boolean;
  pageLoadTime?: number; // in seconds
  mobileResponsive?: boolean;
  
  // Optional: Can be calculated from GSC data
  topKeywordsCount?: number;
  indexedPagesCount?: number;
  
  // Additional data from your system
  backlinksCount?: number;
  seoAuditScore?: number;
  contentCount?: number;
}

export interface SEOScore {
  overall: number; // 0-100
  breakdown: {
    visibility: { score: number; weight: number; };
    performance: { score: number; weight: number; };
    engagement: { score: number; weight: number; };
    technical: { score: number; weight: number; };
  };
  grade: string; // A+, A, B+, B, C, D, F
  insights: string[];
  improvements: string[];
  lastUpdated: string;
  dataQuality: 'excellent' | 'good' | 'limited' | 'poor';
}

export function calculateSEOScore(input: SEOScoreInput): SEOScore {
  const insights: string[] = [];
  const improvements: string[] = [];
  
  // Determine data quality based on available data
  const hasRealData = input.totalImpressions > 0 || input.totalClicks > 0;
  const dataQuality: 'excellent' | 'good' | 'limited' | 'poor' = 
    hasRealData && input.avgPosition > 0 ? 'excellent' :
    hasRealData ? 'good' :
    input.seoAuditScore ? 'limited' : 'poor';
  
  // 1. VISIBILITY SCORE (35% weight)
  // Based on search rankings and presence
  let visibilityScore = 0;
  
  // Average position scoring (0-40 points)
  if (input.avgPosition > 0) {
    if (input.avgPosition <= 3) {
      visibilityScore += 40;
      insights.push('Excellent average ranking position in top 3');
    } else if (input.avgPosition <= 5) {
      visibilityScore += 35;
      insights.push('Strong average ranking in top 5');
    } else if (input.avgPosition <= 10) {
      visibilityScore += 25;
      insights.push('Good ranking on first page');
    } else if (input.avgPosition <= 20) {
      visibilityScore += 15;
      improvements.push('Many keywords on page 2 - optimize for page 1');
    } else {
      visibilityScore += 5;
      improvements.push('Low average ranking - focus on improving keyword positions');
    }
  } else {
    // No position data - use backlinks and content as proxy
    if (input.backlinksCount && input.backlinksCount > 100) {
      visibilityScore += 20;
      insights.push('Good backlink profile indicates strong domain authority');
    } else if (input.backlinksCount && input.backlinksCount > 50) {
      visibilityScore += 15;
    } else if (input.backlinksCount && input.backlinksCount > 10) {
      visibilityScore += 10;
    } else {
      visibilityScore += 5;
      improvements.push('Build more backlinks to improve domain authority');
    }
  }
  
  // Impressions scoring (0-35 points)
  if (input.totalImpressions > 0) {
    if (input.totalImpressions >= 10000) {
      visibilityScore += 35;
      insights.push('High search visibility with 10k+ impressions');
    } else if (input.totalImpressions >= 5000) {
      visibilityScore += 30;
      insights.push('Good search visibility');
    } else if (input.totalImpressions >= 1000) {
      visibilityScore += 20;
    } else if (input.totalImpressions >= 100) {
      visibilityScore += 10;
      improvements.push('Low impressions - expand keyword targeting');
    } else {
      visibilityScore += 5;
      improvements.push('Very low visibility - needs significant SEO work');
    }
  } else {
    // No impression data - use content count as proxy
    if (input.contentCount && input.contentCount > 50) {
      visibilityScore += 20;
      insights.push('Good content volume indicates strong SEO foundation');
    } else if (input.contentCount && input.contentCount > 20) {
      visibilityScore += 15;
    } else if (input.contentCount && input.contentCount > 5) {
      visibilityScore += 10;
    } else {
      visibilityScore += 5;
      improvements.push('Create more content to improve search visibility');
    }
  }
  
  // Keyword diversity (0-25 points)
  if (input.topKeywordsCount) {
    if (input.topKeywordsCount >= 100) {
      visibilityScore += 25;
      insights.push('Excellent keyword diversity with 100+ ranking keywords');
    } else if (input.topKeywordsCount >= 50) {
      visibilityScore += 20;
    } else if (input.topKeywordsCount >= 20) {
      visibilityScore += 15;
    } else if (input.topKeywordsCount >= 10) {
      visibilityScore += 10;
      improvements.push('Limited keyword diversity - create more content');
    } else {
      visibilityScore += 5;
      improvements.push('Very limited keyword targeting');
    }
  } else {
    // Use content count as proxy for keyword diversity
    if (input.contentCount && input.contentCount > 30) {
      visibilityScore += 15;
    } else if (input.contentCount && input.contentCount > 10) {
      visibilityScore += 10;
    } else {
      visibilityScore += 5;
    }
  }
  
  // Normalize to 100
  visibilityScore = Math.min(100, visibilityScore);
  
  // 2. ENGAGEMENT SCORE (25% weight)
  // Based on CTR and click performance
  let engagementScore = 0;
  
  // CTR scoring (0-60 points)
  if (input.avgCTR > 0) {
    const ctrPercent = input.avgCTR;
    if (ctrPercent >= 5) {
      engagementScore += 60;
      insights.push('Excellent CTR - your titles/descriptions are compelling');
    } else if (ctrPercent >= 3) {
      engagementScore += 50;
      insights.push('Good CTR above 3%');
    } else if (ctrPercent >= 2) {
      engagementScore += 35;
    } else if (ctrPercent >= 1) {
      engagementScore += 20;
      improvements.push('Low CTR - optimize title tags and meta descriptions');
    } else {
      engagementScore += 10;
      improvements.push('Very low CTR - titles/descriptions need improvement');
    }
  } else {
    // No CTR data - use content quality as proxy
    if (input.contentCount && input.contentCount > 20) {
      engagementScore += 30;
      insights.push('Good content volume suggests engaging material');
    } else {
      engagementScore += 15;
      improvements.push('Create more engaging content to improve CTR');
    }
  }
  
  // Clicks scoring (0-40 points)
  if (input.totalClicks > 0) {
    if (input.totalClicks >= 1000) {
      engagementScore += 40;
      insights.push('Strong traffic with 1000+ clicks');
    } else if (input.totalClicks >= 500) {
      engagementScore += 35;
      insights.push('Good traffic generation');
    } else if (input.totalClicks >= 100) {
      engagementScore += 25;
    } else if (input.totalClicks >= 50) {
      engagementScore += 15;
    } else if (input.totalClicks >= 10) {
      engagementScore += 10;
      improvements.push('Low click volume - improve rankings and CTR');
    } else {
      engagementScore += 5;
      improvements.push('Very low traffic - needs comprehensive SEO strategy');
    }
  } else {
    // No click data - use backlinks as proxy for traffic potential
    if (input.backlinksCount && input.backlinksCount > 50) {
      engagementScore += 25;
    } else if (input.backlinksCount && input.backlinksCount > 20) {
      engagementScore += 15;
    } else {
      engagementScore += 10;
      improvements.push('Build more backlinks to increase traffic potential');
    }
  }
  
  // Normalize to 100
  engagementScore = Math.min(100, engagementScore);
  
  // 3. PERFORMANCE SCORE (20% weight)
  // Based on clicks vs impressions efficiency
  let performanceScore = 0;
  
  if (input.totalImpressions > 0 && input.totalClicks > 0) {
    // Click efficiency (how well you convert impressions to clicks)
    const clickEfficiency = (input.totalClicks / input.totalImpressions) * 100;
    
    if (clickEfficiency >= 5) {
      performanceScore += 50;
      insights.push('Excellent click-through efficiency');
    } else if (clickEfficiency >= 3) {
      performanceScore += 40;
    } else if (clickEfficiency >= 2) {
      performanceScore += 30;
    } else if (clickEfficiency >= 1) {
      performanceScore += 20;
    } else {
      performanceScore += 10;
      improvements.push('Poor impression-to-click conversion');
    }
    
    // Position vs CTR correlation (are you getting expected CTR for your position?)
    const expectedCTR = calculateExpectedCTR(input.avgPosition);
    const ctrPerformance = (input.avgCTR / expectedCTR) * 100;
    
    if (ctrPerformance >= 100) {
      performanceScore += 50;
      insights.push('CTR exceeds expectations for your average position');
    } else if (ctrPerformance >= 80) {
      performanceScore += 40;
    } else if (ctrPerformance >= 60) {
      performanceScore += 30;
    } else {
      performanceScore += 20;
      improvements.push('CTR below expected for your ranking position');
    }
  } else {
    // No performance data - use SEO audit score as proxy
    if (input.seoAuditScore) {
      performanceScore = input.seoAuditScore * 0.6; // Convert to 0-60 scale
      if (input.seoAuditScore >= 80) {
        insights.push('Strong technical SEO foundation');
      } else if (input.seoAuditScore >= 60) {
        improvements.push('Technical SEO needs improvement');
      } else {
        improvements.push('Significant technical SEO issues to address');
      }
    } else {
      performanceScore = 30; // Neutral score
      improvements.push('Run SEO audit to assess technical performance');
    }
  }
  
  // Normalize to 100
  performanceScore = Math.min(100, performanceScore);
  
  // 4. TECHNICAL SCORE (20% weight)
  // Based on technical SEO factors
  let technicalScore = 0;
  
  // SSL (0-25 points)
  if (input.hasSSL) {
    technicalScore += 25;
  } else {
    technicalScore += 0;
    improvements.push('CRITICAL: Enable HTTPS/SSL certificate');
  }
  
  // Page load time (0-40 points)
  if (input.pageLoadTime !== undefined) {
    if (input.pageLoadTime <= 1) {
      technicalScore += 40;
      insights.push('Excellent page load speed under 1 second');
    } else if (input.pageLoadTime <= 2) {
      technicalScore += 35;
      insights.push('Good page load speed');
    } else if (input.pageLoadTime <= 3) {
      technicalScore += 25;
    } else if (input.pageLoadTime <= 5) {
      technicalScore += 15;
      improvements.push('Page speed could be improved');
    } else {
      technicalScore += 5;
      improvements.push('Poor page speed - optimize images and code');
    }
  } else {
    technicalScore += 20; // Neutral score if not provided
  }
  
  // Mobile responsiveness (0-35 points)
  if (input.mobileResponsive !== undefined) {
    if (input.mobileResponsive) {
      technicalScore += 35;
      insights.push('Mobile-friendly design');
    } else {
      technicalScore += 0;
      improvements.push('CRITICAL: Site not mobile responsive');
    }
  } else {
    technicalScore += 20; // Neutral score if not provided
  }
  
  // Normalize to 100
  technicalScore = Math.min(100, technicalScore);
  
  // Calculate weighted overall score
  const weights = {
    visibility: 0.35,
    engagement: 0.25,
    performance: 0.20,
    technical: 0.20
  };
  
  const overallScore = Math.round(
    visibilityScore * weights.visibility +
    engagementScore * weights.engagement +
    performanceScore * weights.performance +
    technicalScore * weights.technical
  );
  
  // Determine grade
  let grade: string;
  if (overallScore >= 95) grade = 'A+';
  else if (overallScore >= 90) grade = 'A';
  else if (overallScore >= 85) grade = 'A-';
  else if (overallScore >= 80) grade = 'B+';
  else if (overallScore >= 75) grade = 'B';
  else if (overallScore >= 70) grade = 'B-';
  else if (overallScore >= 65) grade = 'C+';
  else if (overallScore >= 60) grade = 'C';
  else if (overallScore >= 55) grade = 'C-';
  else if (overallScore >= 50) grade = 'D+';
  else if (overallScore >= 45) grade = 'D';
  else grade = 'F';
  
  // Add data quality insights
  if (dataQuality === 'poor') {
    improvements.push('Connect Google Search Console for accurate SEO scoring');
  } else if (dataQuality === 'limited') {
    improvements.push('Enable Google Search Console data for more precise insights');
  }
  
  return {
    overall: overallScore,
    breakdown: {
      visibility: { score: Math.round(visibilityScore), weight: weights.visibility },
      engagement: { score: Math.round(engagementScore), weight: weights.engagement },
      performance: { score: Math.round(performanceScore), weight: weights.performance },
      technical: { score: Math.round(technicalScore), weight: weights.technical }
    },
    grade,
    insights,
    improvements,
    lastUpdated: new Date().toISOString(),
    dataQuality
  };
}

// Helper: Expected CTR based on position
function calculateExpectedCTR(position: number): number {
  // Based on industry averages from various studies
  if (position <= 1) return 0.28; // 28%
  if (position <= 2) return 0.15; // 15%
  if (position <= 3) return 0.11; // 11%
  if (position <= 4) return 0.08; // 8%
  if (position <= 5) return 0.07; // 7%
  if (position <= 10) return 0.05; // 5%
  if (position <= 20) return 0.02; // 2%
  return 0.01; // 1%
}

// Helper function to get grade color
export function getGradeColor(grade: string): string {
  switch (grade) {
    case 'A+':
    case 'A':
    case 'A-':
      return 'text-green-600';
    case 'B+':
    case 'B':
    case 'B-':
      return 'text-blue-600';
    case 'C+':
    case 'C':
    case 'C-':
      return 'text-yellow-600';
    case 'D+':
    case 'D':
      return 'text-orange-600';
    case 'F':
      return 'text-red-600';
    default:
      return 'text-gray-600';
  }
}

// Helper function to get score color
export function getScoreColor(score: number): string {
  if (score >= 90) return 'text-green-600';
  if (score >= 80) return 'text-blue-600';
  if (score >= 70) return 'text-yellow-600';
  if (score >= 60) return 'text-orange-600';
  return 'text-red-600';
}


